import { useState, type RefObject } from "react";
import { Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

/** Painted onto the clone, because a serialised SVG has no document to resolve tokens against. */
const INLINED_PROPERTIES = [
  "fill", "fill-opacity", "stroke", "stroke-width", "stroke-dasharray", "stroke-opacity",
  "opacity", "font-family", "font-size", "font-weight", "text-anchor",
] as const;

/**
 * Copies every resolved paint property from a live SVG onto its detached clone.
 *
 * Exported so it can be tested on its own: the failure it prevents is silent — the clipboard still
 * receives a PNG, it is just an empty one — so nothing downstream would notice a regression here.
 */
export function inlinePaintStyles(original: SVGElement, clone: SVGElement): void {
  const originals = [original, ...original.querySelectorAll<SVGElement>("*")];
  const clones = [clone, ...clone.querySelectorAll<SVGElement>("*")];
  originals.forEach((node, i) => {
    const target = clones[i];
    if (!target) return;
    const computed = getComputedStyle(node);
    for (const property of INLINED_PROPERTIES) {
      const value = computed.getPropertyValue(property);
      if (value) target.style.setProperty(property, value);
    }
  });
}

export interface CopyVisualButtonProps {
  /** The element containing the visual (chart, panel) to copy. */
  targetRef: RefObject<HTMLElement | null>;
  /**
   * Series to name in a legend band drawn under the copied image.
   *
   * Optional: a single-series chart needs no legend, and omitting it copies exactly as before. The
   * on-screen `ChartLegend` cannot serve here -- it is HTML outside the `<svg>` that gets copied.
   */
  legend?: CopyLegendItem[];
  /** Button label. Default "Copy chart". */
  label?: string;
  className?: string;
}

/**
 * "Copy this visual to the clipboard" — the control the AUT-429 design reference puts beside
 * Export (`signal-v3/src/components/shared/CopyVisualButton.tsx`).
 *
 * Serialises the first `<svg>` under `targetRef` to a 2x PNG painted on the `--card` token.
 *
 * **Every paint property is copied onto the clone as a literal value first**, which the reference
 * implementation does not do. Charts here stroke their lines with `var(--color-<key>)`, resolved
 * from custom properties that `ChartContainer` writes onto an ancestor `<div>`; the moment the SVG
 * is serialised away from that ancestor those `var()`s resolve to nothing and each line falls back
 * to the SVG default. Measured in Chromium 2026-09-23: the copy still arrives, but it is
 * **monochrome** -- 45 distinct greys against 175 colours with this in place -- so a seasonal chart
 * of four years copies out as four identical grey lines. Reading the *computed* value from the live
 * node and writing it to the clone is what makes the copied image match the screen.
 */
/** One entry in the copied legend: the swatch colour as authored, and what it means. */
export interface CopyLegendItem {
  label: string;
  /** Any CSS colour, `hsl(var(--chart-1))` included -- resolved against the live chart. */
  color: string;
}

/** Layout of the legend band, in CSS pixels before the 2x scale. */
const LEGEND = { height: 34, swatch: 10, gap: 8, between: 20, font: 12 };

/**
 * Resolves a CSS colour **in the chart's own scope**.
 *
 * `hsl(var(--chart-1))` means nothing on a detached node: the custom properties live on ancestors
 * of the chart. The probe is appended inside that scope so `getComputedStyle` returns the rgb the
 * screen is actually painting -- the same reason `inlinePaintStyles` reads computed values.
 */
export function resolveColor(value: string, scope: Element): string {
  const probe = document.createElement("span");
  probe.style.color = value;
  probe.style.display = "none";
  scope.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();

  return resolved || value;
}

/**
 * Paints the legend band beneath the chart.
 *
 * Split out because jsdom has no 2D canvas context, so the copy path itself cannot be exercised in
 * a unit test -- this takes the context as an argument and is tested against a recording stub.
 * Items are laid out on one centred row, measured before drawing so the row really is centred.
 */
export function drawLegend(
  ctx: CanvasRenderingContext2D,
  items: CopyLegendItem[],
  options: { width: number; top: number; scale: number; textColor: string },
): void {
  const { width, top, scale, textColor } = options;
  const font = `${LEGEND.font * scale}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  ctx.font = font;
  ctx.textBaseline = "middle";

  const widths = items.map((item) => ctx.measureText(item.label).width);
  const itemWidths = widths.map((w) => LEGEND.swatch * scale + LEGEND.gap * scale + w);
  const total = itemWidths.reduce((sum, w) => sum + w, 0) + LEGEND.between * scale * (items.length - 1);

  let x = (width - total) / 2;
  const centre = top + (LEGEND.height * scale) / 2;

  items.forEach((item, i) => {
    ctx.fillStyle = item.color;
    ctx.fillRect(x, centre - (LEGEND.swatch * scale) / 2, LEGEND.swatch * scale, LEGEND.swatch * scale);

    ctx.fillStyle = textColor;
    ctx.fillText(item.label, x + LEGEND.swatch * scale + LEGEND.gap * scale, centre);

    x += itemWidths[i] + LEGEND.between * scale;
  });
}

export function CopyVisualButton({ targetRef, legend, label = "Copy chart", className }: CopyVisualButtonProps) {
  const [copying, setCopying] = useState(false);

  const handleCopy = async () => {
    const svg = targetRef.current?.querySelector("svg");
    if (!svg) {
      toast.error("Nothing to copy", { description: "The visual isn't ready yet." });
      return;
    }
    setCopying(true);
    try {
      const rect = svg.getBoundingClientRect();
      const clone = svg.cloneNode(true) as SVGSVGElement;
      clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      clone.setAttribute("width", String(rect.width));
      clone.setAttribute("height", String(rect.height));

      inlinePaintStyles(svg, clone);

      const background = `hsl(${getComputedStyle(document.body).getPropertyValue("--card").trim()})`;
      const source = new XMLSerializer().serializeToString(clone);

      const img = new Image();
      img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(source);
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      const scale = 2;
      // The legend is an HTML `<div>` beside the chart (`ChartLegend`), not part of the `<svg>`, so
      // serialising the SVG drops it -- a four-year seasonal chart copied out as four coloured
      // lines with nothing saying which year was which. Reported from dev 2026-09-25. The band is
      // drawn onto the canvas instead of captured, because the on-screen legend is not in the tree
      // being serialised.
      const items = (legend ?? []).filter((item) => item.label.trim() !== "");
      const band = items.length > 0 ? LEGEND.height : 0;

      const canvas = document.createElement("canvas");
      canvas.width = rect.width * scale;
      canvas.height = (rect.height + band) * scale;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("no canvas context");
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, rect.width * scale, rect.height * scale);

      if (items.length > 0) {
        const scope = targetRef.current ?? document.body;
        drawLegend(
          ctx,
          items.map((item) => ({ label: item.label, color: resolveColor(item.color, scope) })),
          {
            width: canvas.width,
            top: rect.height * scale,
            scale,
            textColor: resolveColor(
              `hsl(${getComputedStyle(document.body).getPropertyValue("--muted-foreground").trim()})`,
              scope,
            ),
          },
        );
      }

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("no blob");
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      toast.success("Copied to clipboard", { description: "Paste it into a doc, deck or message." });
    } catch (error) {
      // Four distinct failures reach here -- the image never loaded, no canvas context, no blob,
      // and an actual clipboard denial. The toast names the likeliest because it is the only one a
      // viewer can act on, but the real cause goes to the console rather than being swallowed.
      console.error("CopyVisualButton: could not copy the visual", error);
      toast.error("Could not copy", { description: "Your browser blocked the clipboard image." });
    } finally {
      setCopying(false);
    }
  };

  return (
    <Button variant="ghost" size="sm" className={className} onClick={handleCopy} disabled={copying}>
      {copying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
      {label}
    </Button>
  );
}
